import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { MdUnfoldMore } from "react-icons/md";

// Small pieces shared by the Team Insights tabs.

export const rupees = (v) => (v === null || v === undefined ? "—" : `₹${Math.round(v).toLocaleString("en-IN")}`);
export const fmt = (v, d = 0) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN", { maximumFractionDigits: d }));
export const hrs = (v) => {
  if (v === null || v === undefined) return "—";
  if (v < 1) return `${Math.round(v * 60)} min`;
  if (v < 48) return `${fmt(v, 1)} h`;
  return `${fmt(v / 24, 1)} days`;
};
export const mins = (v) => (v === null || v === undefined ? "—" : `${Math.round(v)} min`);
export const clockOf = (m) => (m === null || m === undefined ? "—" : `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);

export const Card = ({ title, hint, right, children }) => (
  <section className="bg-white rounded-xl border border-gray-100 shadow-sm">
    {(title || right) && (
      <header className="flex flex-wrap items-start justify-between gap-2 px-4 pt-4">
        <div>
          <h2 className="font-semibold text-gray-900">{title}</h2>
          {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
        </div>
        {right}
      </header>
    )}
    <div className="p-4">{children}</div>
  </section>
);

export const Kpi = ({ label, value, sub, valueClass = "text-gray-900" }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
    <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
    <p className={`text-3xl font-semibold mt-1 tabular-nums ${valueClass}`}>{value}</p>
    {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
  </div>
);

// 0-100 score with a colour that reads at a glance.
export const scoreClass = (v) => (v === null || v === undefined ? "text-gray-300" : v >= 80 ? "text-emerald-600" : v >= 60 ? "text-amber-600" : "text-red-600");

// Runs one report query; returns { data, loading, error } with the JSON.
export const useReport = (query, field, filter) => {
  const { data, loading, error } = useQuery(query, { variables: { filter }, fetchPolicy: "cache-and-network" });
  const res = data?.[field];
  return {
    data: res?.status === "SUCCESS" ? res.data : null,
    loading: loading && !data,
    error: error?.message || (res?.status === "ERROR" ? res.message : null),
  };
};

export const ReportState = ({ loading, error, data, children }) => {
  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">Could not load: {error}</div>;
  if (loading || !data) return <div className="py-16 text-center text-gray-400">Loading…</div>;
  const failed = (data.sections || []).filter((s) => !s.ok);
  return (
    <div className="space-y-5">
      {failed.length > 0 && (
        <p className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2">
          Couldn't load: {failed.map((f) => f.label).join(", ")}. Everything else on this tab is complete.
        </p>
      )}
      {children}
    </div>
  );
};

// Sortable table. columns: [{ key, label, title, cell(row), sort(row) }].
export const SortTable = ({ columns, rows, initialSort, rowKey, nameCell, empty = "Nothing in this period.", rowClass }) => {
  const [sort, setSort] = useState(initialSort || { key: columns[0].key, dir: -1 });
  const sorted = useMemo(() => {
    const col = columns.find((c) => c.key === sort.key);
    const val = col?.sort || ((r) => r[sort.key]);
    return (rows || []).slice().sort((a, b) => {
      const x = val(a);
      const y = val(b);
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      if (typeof x === "string") return x.localeCompare(y) * sort.dir;
      return (x - y) * sort.dir;
    });
  }, [rows, columns, sort]);
  const toggle = (key) => setSort((cur) => (cur.key === key ? { key, dir: -cur.dir } : { key, dir: -1 }));
  return (
    <div className="overflow-x-auto -mx-4">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
            {nameCell && <th className="px-4 py-2 font-medium sticky left-0 bg-white">{nameCell.label}</th>}
            {columns.map((c) => (
              <th key={c.key} title={c.title} className="px-3 py-2 font-medium whitespace-nowrap">
                <button type="button" onClick={() => toggle(c.key)} className={`inline-flex items-center gap-0.5 hover:text-gray-900 ${sort.key === c.key ? "text-gray-900" : ""}`}>
                  {c.label}
                  {sort.key === c.key ? <span>{sort.dir < 0 ? "↓" : "↑"}</span> : <MdUnfoldMore className="text-gray-300" />}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => (
            <tr key={rowKey(r)} className={`border-b border-gray-50 hover:bg-gray-50 ${rowClass ? rowClass(r) : ""}`}>
              {nameCell && <td className="px-4 py-2 sticky left-0 bg-white whitespace-nowrap">{nameCell.cell(r)}</td>}
              {columns.map((c) => (
                <td key={c.key} className="px-3 py-2 tabular-nums whitespace-nowrap">
                  {c.cell ? c.cell(r) : fmt(r[c.key])}
                </td>
              ))}
            </tr>
          ))}
          {!sorted.length && (
            <tr>
              <td colSpan={columns.length + 1} className="px-4 py-8 text-center text-gray-400">{empty}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// A professional's name that narrows the page to them.
export const PersonButton = ({ row, onPick, children }) => (
  <button type="button" onClick={() => onPick && onPick(row.employeeId)} className="font-medium text-gray-900 hover:underline text-left">
    {children || row.name || `#${row.employeeId}`}
    {row.status && row.status !== "ACTIVE" && <span className="ml-1.5 text-[11px] font-normal text-gray-400">{String(row.status).toLowerCase()}</span>}
  </button>
);

// Booking id that opens the booking.
export const BookingLink = ({ row, onOpen }) => (
  <button type="button" onClick={() => onOpen && onOpen(row)} className="font-mono text-xs text-sky-700 hover:underline">
    {row.bookingId || "open"}
  </button>
);
